import { TopicInfo } from 'schemas/TopicInfoSchema'
import ReturnStatus from 'enums/ReturnStatus'
import CrosstabDisplayType, { CrosstabDisplayTypeStrings } from 'enums/CrosstabDisplayType'
import CrosstabDisplayTypeDetail, {
  CrosstabDisplayTypeDetailStrings
} from 'enums/CrosstabDisplayTypeDetail'
import ComparisonMode, { ComparisonModeStrings } from 'enums/ComparisonMode'
import DisplayOption from 'enums/DisplayOption'
import CrosstabSortOption from 'enums/CrosstabSortOption'
import { InsightViewOptionStrings } from 'enums/InsightViewOption'
import {
  ROW_GRAND_TOTAL_ID,
  SUPPORT_WEIGHING_OPTIONS,
  DEFAULT_WEIGHTING,
  CALCULATED_OPTIONS
} from '../constants'
import { SeriesData, PivotWithCellMarker, CrosstabWidgetsType } from '../../types'
import { getSettingTopicInfos } from './crosstabDataUtils'

const getRowId = (data: PivotWithCellMarker, rowComparisonMode: ComparisonModeStrings) => {
  if (!data) {
    return ''
  }

  switch (rowComparisonMode) {
    case ComparisonMode.THEMES:
      return data.rowThemeId!.toString()
    case ComparisonMode.TOPICS:
      return data.topicId!.toString()
    case ComparisonMode.TAGS:
      return data.rowTagName!
    case ComparisonMode.DOC_HIERARCHIES:
      return data.rowDocName!
    default:
      return ''
  }
}

const getColumnId = (data: PivotWithCellMarker, colComparisonMode: ComparisonModeStrings) => {
  if (!data) {
    return ''
  }
  if (data.cellMarker === CrosstabDisplayTypeDetail.ROW_GRAND_TOTAL) {
    return ROW_GRAND_TOTAL_ID
  }
  switch (colComparisonMode) {
    case ComparisonMode.THEMES:
      return data.themeId!.toString()
    case ComparisonMode.TAGS:
      return data.tagName!
    case ComparisonMode.DOC_HIERARCHIES:
      return data.docName!
    default:
      return ''
  }
}

const findPivot = (
  result: PivotWithCellMarker[],
  rowId: string,
  colId: string,
  rowComparisonMode: ComparisonModeStrings,
  colComparisonMode: ComparisonModeStrings
) =>
  result.find((pivot) => {
    if (rowId === getRowId(pivot, rowComparisonMode)) {
      if (colId === getColumnId(pivot, colComparisonMode)) {
        return true
      }
      if (colComparisonMode === ComparisonMode.NONE && colId !== ROW_GRAND_TOTAL_ID) {
        return true
      }
    }
    return false
  }) || null

export const arrangeResult = (
  result: PivotWithCellMarker[],
  rowIds: string[],
  colIds: string[],
  rowComparisonMode: ComparisonModeStrings,
  colComparisonMode: ComparisonModeStrings
) => {
  const retVal: (PivotWithCellMarker | null)[][] = []
  if (!result) {
    return retVal
  }

  for (let i = 0; i < rowIds.length; i += 1) {
    retVal[i] = [] as PivotWithCellMarker[]

    for (let j = 0; j < colIds.length; j += 1) {
      retVal[i].push(findPivot(result, rowIds[i], colIds[j], rowComparisonMode, colComparisonMode))
    }
  }

  return retVal
}

/*
Keep this function so it can be still used if somedays the
view option does not work with some types of topics
*/
const getDisabledTopicIds = (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  topicInfos: TopicInfo[],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  viewOption: InsightViewOptionStrings
): string[] => []

const getThemeWeightingMap = (widgetSetting: CrosstabWidgetsType) => {
  const weightingMap: Map<number, number> = new Map()
  if (!SUPPORT_WEIGHING_OPTIONS.includes(widgetSetting.viewOption!)) {
    return weightingMap
  }

  if (widgetSetting.pivotThemeInfos && widgetSetting.pivotThemeInfos.length > 0) {
    widgetSetting.pivotThemeInfos.forEach((theme) =>
      weightingMap.set(theme.themeId!, theme.weighting!)
    )
  }

  if (widgetSetting.pivotRowThemeInfos && widgetSetting.pivotRowThemeInfos.length > 0) {
    widgetSetting.pivotRowThemeInfos.forEach((theme) =>
      weightingMap.set(theme.themeId!, theme.weighting!)
    )
  }
  return weightingMap
}

const isRowGrandTotalPivot = (pivot: PivotWithCellMarker) =>
  pivot.cellMarker === CrosstabDisplayTypeDetail.ROW_GRAND_TOTAL

const getCell = (
  x: number,
  y: number,
  value: number,
  disabled: boolean,
  noCount: boolean,
  isRowGrandTotal: boolean,
  bsNotSupported: boolean,
  isTotal: boolean,
  opt_rowWeighting?: number,
  opt_colWeighting?: number
): SeriesData => {
  const series: SeriesData = {
    x,
    y,
    value: disabled ? 0 : value,
    disable: disabled,
    noCount,
    isRowGrandTotal,
    bsNotSupported,
    total: isTotal,
    rowWeighting: undefined === opt_rowWeighting ? DEFAULT_WEIGHTING : opt_rowWeighting,
    colWeighting: undefined === opt_colWeighting ? DEFAULT_WEIGHTING : opt_colWeighting
  }

  if (noCount) {
    series.color = '#999999'
  }

  return series
}

const getCategoryIdx = (seriesItem: SeriesData, totalType: CrosstabDisplayTypeDetailStrings) => {
  switch (totalType) {
    case CrosstabDisplayTypeDetail.COLUMNS:
      return seriesItem.y
    default:
    case CrosstabDisplayTypeDetail.ROWS:
      return seriesItem.x
  }
}

const getCategorySubIdx = (seriesItem: SeriesData, totalType: CrosstabDisplayTypeDetailStrings) => {
  switch (totalType) {
    case CrosstabDisplayTypeDetail.COLUMNS:
      return seriesItem.x
    default:
    case CrosstabDisplayTypeDetail.ROWS:
      return seriesItem.y
  }
}

// TODO: maybe refactor for better readability. eg. groupDataBy instead of totalType
/**
 *      C1  C2  C3
 *  R1  -   -   -
 *  R2  -   -   -
 *  R3  -   -   -
 * The function is to group data by column or row.
 * When totalType = ROWS,NONE, the definition of "total" is to aggregate data by column.
 * Thus the result will be like [[R1C1,R2C1,R3C1],[R1C2,R2C2,R3C2],[R1C3,R2C3,R3C3]]
 */
export const getCategorizedSeries = (
  seriesData: SeriesData[],
  totalType: CrosstabDisplayTypeDetailStrings
): SeriesData[][] => {
  const map = new Map()

  if (!seriesData) {
    return []
  }

  seriesData.forEach((data) => {
    const id = getCategoryIdx(data, totalType)
    const subId = getCategorySubIdx(data, totalType)
    if (!map.has(id)) {
      map.set(id, [])
    }
    map.get(id)[subId] = data
  })

  return Array.from(map.values())
}

const getWeighting = (seriesItem: SeriesData, totalType: CrosstabDisplayTypeDetailStrings) => {
  switch (totalType) {
    case CrosstabDisplayTypeDetail.COLUMNS:
      return seriesItem.rowWeighting
    default:
    case CrosstabDisplayTypeDetail.ROWS:
      return seriesItem.colWeighting
  }
}

export const isCellDisabled = (seriesData: SeriesData) =>
  seriesData.disable || seriesData.noCount || seriesData.bsNotSupported

const isCellSkippedForTotal = (seriesData: SeriesData) =>
  seriesData.isRowGrandTotal || isCellDisabled(seriesData)

const getSeriesTotal = (seriesData: SeriesData[], totalType: CrosstabDisplayTypeDetailStrings) => {
  let total = 0

  if (!seriesData || seriesData.length === 0) {
    return total
  }

  const rowGrandTotalSeriesData = seriesData.find((data) => data.isRowGrandTotal)
  if (rowGrandTotalSeriesData) {
    return rowGrandTotalSeriesData.value
  }

  seriesData
    .filter((data) => !isCellSkippedForTotal(data))
    .forEach((data) => {
      const weighting = getWeighting(data, totalType)
      total += data.value * (weighting / DEFAULT_WEIGHTING)
    })
  return total
}

const displayAverage = (viewOption: InsightViewOptionStrings) =>
  CALCULATED_OPTIONS.includes(viewOption)

const getTotalValue = (
  seriesData: SeriesData[],
  viewOption: InsightViewOptionStrings,
  valueType: CrosstabDisplayTypeStrings,
  totalType: CrosstabDisplayTypeDetailStrings
): number => {
  let total = getSeriesTotal(seriesData, totalType)
  let cellsWithCount = 0
  switch (valueType) {
    case CrosstabDisplayType.PERCENT_SHARE:
      total = total === 0 ? 0 : 100
      break
    default:
    case CrosstabDisplayType.VALUE:
      seriesData
        .filter((data) => !isCellSkippedForTotal(data))
        .forEach((data) => {
          const weighting = getWeighting(data, totalType)
          cellsWithCount += weighting / DEFAULT_WEIGHTING
        })
      if (displayAverage(viewOption)) {
        total = cellsWithCount !== 0 ? total / cellsWithCount : 0
      }
      break
  }
  return total
}

/**
 * when displaying Row totals, shift every row up so that total can fit in (x,0)
 */
const shiftSeriesPosition = (
  rowOrCol: SeriesData[],
  totalType: CrosstabDisplayTypeDetailStrings
) => {
  if (!rowOrCol) {
    return
  }

  if (totalType === CrosstabDisplayTypeDetail.ROWS) {
    rowOrCol
      .filter((data) => !data.total)
      .forEach((data) => {
        // eslint-disable-next-line no-param-reassign
        data.y += 1
      })
  }
}

const getTotalX = (seriesData: SeriesData[], totalType: CrosstabDisplayTypeDetailStrings) => {
  const categoryIdx = getCategoryIdx(seriesData[0], totalType)

  switch (totalType) {
    case CrosstabDisplayTypeDetail.ROWS:
      return categoryIdx
    default:
    case CrosstabDisplayTypeDetail.COLUMNS:
      return seriesData.length
  }
}

const getTotalY = (
  seriesData: SeriesData[],
  totalType: CrosstabDisplayTypeDetailStrings,
  isReversed: boolean
) => {
  const categoryIdx = getCategoryIdx(seriesData[0], totalType)

  switch (totalType) {
    case CrosstabDisplayTypeDetail.ROWS:
      return isReversed ? 0 : seriesData.length
    default:
    case CrosstabDisplayTypeDetail.COLUMNS:
      return categoryIdx
  }
}

const isTotalDisabled = (seriesData: SeriesData[]) => {
  if (!seriesData) {
    return false
  }
  return seriesData.every((data) => data.disable)
}

const isTotalNoCount = (seriesData: SeriesData[]) => {
  if (!seriesData) {
    return false
  }
  return seriesData.every((data) => data.noCount)
}

const isTotalNoCountBSTopic = (seriesData: SeriesData[]) => {
  if (!seriesData) {
    return false
  }
  return seriesData.every((data) => data.bsNotSupported)
}

export const genTotal = (
  seriesData: SeriesData[],
  valueType: CrosstabDisplayTypeStrings,
  totalType: CrosstabDisplayTypeDetailStrings,
  viewOption: InsightViewOptionStrings,
  isReversed: boolean
) => {
  if (!seriesData) {
    return
  }

  if (totalType === CrosstabDisplayTypeDetail.NONE) {
    return
  }

  const categorizedSeries = getCategorizedSeries(seriesData, totalType)

  categorizedSeries.forEach((rowOrCol) => {
    const total = getTotalValue(rowOrCol, viewOption, valueType, totalType)

    // shift existing cells
    if (isReversed) {
      shiftSeriesPosition(rowOrCol, totalType)
    }

    // get total cell
    const x = getTotalX(rowOrCol, totalType)
    const y = getTotalY(rowOrCol, totalType, isReversed)

    const totalCell = getCell(
      x,
      y,
      total,
      isTotalDisabled(rowOrCol),
      isTotalNoCount(rowOrCol),
      false,
      isTotalNoCountBSTopic(rowOrCol),
      true
    )
    seriesData.push(totalCell)
  })
}

const sortRows = (
  datas: SeriesData[],
  ids: string[],
  names: string[],
  length: number,
  isDescending: boolean
) => {
  const map: (SeriesData | string)[][] = []

  // Step 1. Put the series data into a 2-dimensional array
  datas.forEach((data) => {
    const { x, y } = data
    if (!map[y]) {
      map[y] = []
    }
    map[y][x] = data
  })

  // Step 2. Also put names and ids into the array
  for (let i = 0; i < names.length; i += 1) {
    map[i][length] = names[i]
    map[i][length + 1] = ids[i]
  }

  // Step 3. Sort the 1st dimension arry with the average value.
  if (isDescending) {
    map.sort((a, b) => {
      const dataA = a[length - 1] as SeriesData
      const dataB = b[length - 1] as SeriesData
      return dataA.value - dataB.value
    })
  } else {
    map.sort((a, b) => {
      const dataA = a[length - 1] as SeriesData
      const dataB = b[length - 1] as SeriesData
      return dataB.value - dataA.value
    })
  }

  // Step 4. Update the series data with new XY
  for (let i = 0; i < map.length; i += 1) {
    const subMap = map[i]
    for (let j = 0; j < subMap.length; j += 1) {
      const data = map[i][j]
      if (typeof data === 'object') {
        data.x = j
        data.y = i
      }
    }
    // eslint-disable-next-line no-param-reassign
    names[i] = map[i][length] as string
    // eslint-disable-next-line no-param-reassign
    ids[i] = map[i][length + 1] as string
  }
}

const sortColumns = (
  datas: SeriesData[],
  ids: string[],
  names: string[],
  length: number,
  isDescending: boolean,
  opt_isFormatedResult?: boolean
) => {
  const map: (SeriesData | string)[][] = []

  // Step 1. Put the series data into a 2-dimensional array
  datas.forEach((data) => {
    const { x, y } = data
    if (!map[x]) {
      map[x] = []
    }
    map[x][y] = data
  })

  // Step 2. Also put names into the array
  for (let i = 0; i < names.length; i += 1) {
    map[i][length] = names[i]
    map[i][length + 1] = ids[i]
  }

  // Step 3. Sort the 1st dimension arry with the average value.
  if (isDescending) {
    if (opt_isFormatedResult) {
      map.sort((a, b) => {
        const dataA = a[length - 1] as SeriesData
        const dataB = b[length - 1] as SeriesData
        return dataB.value - dataA.value
      })
    } else {
      map.sort((a, b) => {
        const dataA = a[0] as SeriesData
        const dataB = b[0] as SeriesData
        return dataB.value - dataA.value
      })
    }
  } else if (opt_isFormatedResult) {
    map.sort((a, b) => {
      const dataA = a[length - 1] as SeriesData
      const dataB = b[length - 1] as SeriesData
      return dataA.value - dataB.value
    })
  } else {
    map.sort((a, b) => {
      const dataA = a[0] as SeriesData
      const dataB = b[0] as SeriesData
      return dataA.value - dataB.value
    })
  }

  // Step 4. Update the series data with new XY
  for (let i = 0; i < map.length; i += 1) {
    const subMap = map[i] as SeriesData[]
    for (let j = 0; j < subMap.length; j += 1) {
      const data = map[i][j] as SeriesData
      if (typeof data === 'object') {
        data.x = i
        data.y = j
      }
    }
    // eslint-disable-next-line no-param-reassign
    names[i] = map[i][length] as string
    // eslint-disable-next-line no-param-reassign
    ids[i] = map[i][length + 1] as string
  }
}

export const sortByMetric = (
  seriesData: SeriesData[],
  widgetSetting: CrosstabWidgetsType,
  rowIds: string[],
  rowNames: string[],
  colIds: string[],
  colNames: string[],
  opt_isFormatedResult?: boolean
) => {
  let displayTotal = widgetSetting.ctDisplayTotal
  const hasTempTotal =
    displayTotal === CrosstabDisplayTypeDetail.NONE ||
    widgetSetting.displayOption !== DisplayOption.TABLE
  if (displayTotal === CrosstabDisplayTypeDetail.NONE) {
    // Even the total value isn't shown,
    // the data should be sorted by column total / average
    // We have to modify displayTotal to get the temporary total cell
    if (
      widgetSetting.ctSort === CrosstabSortOption.COLUMN_METRIC_DES ||
      widgetSetting.ctSort === CrosstabSortOption.COLUMN_METRIC_ASC
    ) {
      displayTotal = CrosstabDisplayTypeDetail.COLUMNS
    } else {
      displayTotal = CrosstabDisplayTypeDetail.ROWS
    }
  }

  // insert total cell
  genTotal(
    seriesData,
    widgetSetting.ctDisplay!,
    displayTotal!,
    widgetSetting.viewOption!,
    !opt_isFormatedResult
  )

  // Must switch on the resolved displayTotal, not widgetSetting.ctDisplayTotal: genTotal above was
  // given the resolved value, and the cleanup below removes whichever total cell genTotal inserted.
  // Reading the raw setting here sends a NONE + column-metric sort into the ROWS branch, which
  // deletes the y===0 cells as if they were a total row when they are real data (NI-2686).
  switch (displayTotal) {
    case CrosstabDisplayTypeDetail.COLUMNS: {
      if (hasTempTotal) {
        // Add a temporary name 'Total';
        if (opt_isFormatedResult) {
          colNames.push('Total')
        } else {
          colNames.unshift('Total')
        }
      }
      const shouldInvertSort =
        (opt_isFormatedResult && widgetSetting.displayOption === DisplayOption.TABLE) ||
        widgetSetting.displayOption !== DisplayOption.TABLE

      const isDescending = widgetSetting.ctSort === CrosstabSortOption.COLUMN_METRIC_DES
      sortRows(
        seriesData,
        rowIds,
        rowNames,
        colNames.length,
        shouldInvertSort ? !isDescending : isDescending
      )
      if (hasTempTotal && !opt_isFormatedResult) {
        // Remove the temporary total cell
        colNames.splice(0, 1)
        for (let i = seriesData.length - 1; i >= 0; i -= 1) {
          if (seriesData[i].x === colNames.length) {
            seriesData.splice(i, 1)
          }
        }
      }
      break
    }
    case CrosstabDisplayTypeDetail.ROWS:
    case CrosstabDisplayTypeDetail.NONE:
    default: {
      if (hasTempTotal) {
        // Add a temporary name 'Total';
        if (opt_isFormatedResult) {
          rowNames.push('Total')
        } else {
          rowNames.unshift('Total')
        }
      }

      sortColumns(
        seriesData,
        colIds,
        colNames,
        rowNames.length,
        widgetSetting.ctSort === CrosstabSortOption.ROW_METRIC_DES,
        opt_isFormatedResult
      )

      if (hasTempTotal) {
        // Remove the temporary total cell and temporary name 'Total'
        if (opt_isFormatedResult) {
          if (widgetSetting.displayOption === DisplayOption.TABLE) {
            rowNames.splice(rowNames.length - 1, 1)
            for (let i = seriesData.length - 1; i >= 0; i -= 1) {
              if (seriesData[i].y === rowNames.length) {
                seriesData.splice(i, 1)
              }
            }
          }
        } else {
          rowNames.splice(0, 1)
          for (let i = seriesData.length - 1; i >= 0; i -= 1) {
            if (seriesData[i].y === 0) {
              seriesData.splice(i, 1)
            }
          }

          for (let i = 0; i < seriesData.length; i += 1) {
            // eslint-disable-next-line no-param-reassign
            seriesData[i].y -= 1
          }
        }
      }
      break
    }
  }
}

const convertValueFormat = (
  seriesData: SeriesData[],
  valueType: CrosstabDisplayTypeStrings,
  totalType: CrosstabDisplayTypeDetailStrings,
  percentShareType: CrosstabDisplayTypeDetailStrings
) => {
  if (!seriesData) {
    return
  }

  if (valueType === CrosstabDisplayType.VALUE) {
    return
  }

  if (valueType === CrosstabDisplayType.PERCENT_SHARE) {
    if (percentShareType === CrosstabDisplayTypeDetail.ROW_GRAND_TOTAL) {
      // eslint-disable-next-line no-param-reassign
      totalType = CrosstabDisplayTypeDetail.COLUMNS
    } else if (totalType === CrosstabDisplayTypeDetail.NONE) {
      // eslint-disable-next-line no-param-reassign
      totalType = percentShareType
    }
    const categorizedSeries = getCategorizedSeries(seriesData, totalType)
    categorizedSeries.forEach((rowOrCol) => {
      const total = getSeriesTotal(rowOrCol, totalType)

      rowOrCol
        .filter((cell) => !cell.isRowGrandTotal)
        .forEach((cell) => {
          // eslint-disable-next-line no-param-reassign
          cell.value = total !== 0 ? (cell.value * DEFAULT_WEIGHTING) / total : 0
        })
    })
  }
}

export const genSeriesData = (
  arrangedResults: (PivotWithCellMarker | null)[][],
  widgetSetting: CrosstabWidgetsType,
  opt_isBarColumnChart?: boolean
) => {
  const disabledTopicIds = getDisabledTopicIds(
    getSettingTopicInfos(widgetSetting),
    widgetSetting.viewOption!
  )
  const weightingMap = getThemeWeightingMap(widgetSetting)
  const seriesData = []

  for (let i = 0; i < arrangedResults.length; i += 1) {
    const resultRow = arrangedResults[i]

    for (let j = 0; j < resultRow.length; j += 1) {
      const resultCell = resultRow[j]
      if (resultCell) {
        const { topicId } = resultCell
        const value = resultCell.metrics![0].dataset![0].set![0]!
        const disabled = disabledTopicIds.includes(topicId!.toString())
        const noCount = resultCell.metrics![0].dataset![0].status === ReturnStatus.NO_COUNT
        const languageNotSupported =
          resultCell.metrics![0].dataset![0].status === ReturnStatus.LANGUAGE_NOT_SUPPORTED
        const isRowGrandTotal = isRowGrandTotalPivot(resultCell)

        let rowWeighting
        if (resultCell.themeId) {
          rowWeighting = weightingMap.get(resultCell.themeId)
        }
        let colWeighting
        if (resultCell.rowThemeId) {
          colWeighting = weightingMap.get(resultCell.rowThemeId)
        }

        seriesData.push(
          getCell(
            j,
            i,
            value,
            disabled,
            noCount,
            isRowGrandTotal,
            languageNotSupported,
            false,
            rowWeighting,
            colWeighting
          )
        )
      } else {
        const isLastColumn = j === resultRow.length - 1
        const isRowGrandTotal =
          widgetSetting.ctPercentShare === CrosstabDisplayTypeDetail.ROW_GRAND_TOTAL && isLastColumn

        seriesData.push(
          getCell(j, i, 0, false, false, isRowGrandTotal, false, false, undefined, undefined)
        )
      }
    }
  }
  // convert value to percent
  // bar column chart will switch to percent on configure
  // however we still need to calculate percent for excel
  if (!opt_isBarColumnChart) {
    convertValueFormat(
      seriesData,
      widgetSetting.ctDisplay!,
      widgetSetting.ctDisplayTotal!,
      widgetSetting.ctPercentShare!
    )
  }
  return seriesData
}
